import React, { useState } from "react";
import styles from "./DevServices.module.css";
import {
  FiCode, FiSmartphone, FiCpu, FiLayers, FiShield, FiDatabase,
  FiMail, FiPhone, FiArrowRight, FiCheck, FiZap, FiUsers,
  FiRefreshCw, FiHeadphones, FiDollarSign, FiCopy, FiCheckCircle,
  FiGlobe, FiPackage,
} from "react-icons/fi";

const services = [
  {
    icon: <FiGlobe />,
    title: "Web Application Development",
    color: "#5540C7",
    tag: "Full-Stack",
    description:
      "From landing pages to complex SaaS platforms — we design, build, and deploy web apps that scale. React, Node.js, Next.js, and more.",
    highlights: ["Custom UI/UX Design", "React & Next.js Frontend", "Node.js / Django Backend", "REST & GraphQL APIs", "Cloud Deployment (AWS / GCP)"],
  },
  {
    icon: <FiSmartphone />,
    title: "Mobile App Development",
    color: "#240BAD",
    tag: "iOS & Android",
    description:
      "Cross-platform mobile apps that feel native. We build, test, and publish your app to both the App Store and Google Play Store.",
    highlights: ["React Native / Flutter", "Custom UI Components", "Push Notifications & Auth", "Offline-First Architecture", "App Store Submission"],
  },
  {
    icon: <FiLayers />,
    title: "Blockchain & Web3 Products",
    color: "#8a75f6",
    tag: "Web3",
    description:
      "Smart contracts, DeFi protocols, NFT marketplaces, and full-stack DApps built on Ethereum, Polygon, Solana, and other chains.",
    highlights: ["Smart Contract Development", "Token & NFT Platforms", "DApp Frontend (ethers.js / wagmi)", "Audit-Ready Code", "Multi-Chain Support"],
  },
  {
    icon: <FiCpu />,
    title: "AI-Powered Solutions",
    color: "#5540C7",
    tag: "AI / ML",
    description:
      "We integrate AI and ML into your products — from intelligent chatbots to recommendation engines and predictive analytics dashboards.",
    highlights: ["LLM & Chatbot Integration", "Custom ML Models", "Computer Vision Pipelines", "Data Dashboards & Insights", "OpenAI / HuggingFace APIs"],
  },
  {
    icon: <FiDatabase />,
    title: "Database & Backend Systems",
    color: "#240BAD",
    tag: "Backend",
    description:
      "Robust, high-performance backend systems and data architectures. We build APIs and databases that handle millions of requests reliably.",
    highlights: ["PostgreSQL / MongoDB / MySQL", "Scalable REST & gRPC APIs", "Microservices Architecture", "Real-time with WebSockets", "Performance Optimisation"],
  },
  {
    icon: <FiShield />,
    title: "Security & Code Audits",
    color: "#8a75f6",
    tag: "Security",
    description:
      "We review your existing codebase for vulnerabilities, OWASP issues, and smart contract risks — and fix them before they become a problem.",
    highlights: ["OWASP Top 10 Review", "Smart Contract Audits", "Penetration Testing Basics", "Auth & Data Security", "Detailed Audit Reports"],
  },
];

const whyUs = [
  {
    icon: <FiDollarSign />,
    title: "Low-Cost, High Quality",
    desc: "We keep our overhead lean so you get agency-grade work at a fraction of the typical cost — without hidden fees.",
  },
  {
    icon: <FiZap />,
    title: "Fast Delivery",
    desc: "We move quickly. Most MVPs and small products are delivered within 2–6 weeks depending on scope.",
  },
  {
    icon: <FiRefreshCw />,
    title: "Iterative Process",
    desc: "We work in short sprints with regular demos so you always know exactly where your product stands.",
  },
  {
    icon: <FiUsers />,
    title: "Dedicated Team",
    desc: "You get a small, focused team of engineers who own your product end-to-end — not a revolving door of contractors.",
  },
  {
    icon: <FiHeadphones />,
    title: "Post-Launch Support",
    desc: "We don't disappear after delivery. Every engagement includes post-launch bug fixes and dedicated support.",
  },
  {
    icon: <FiPackage />,
    title: "Full Ownership",
    desc: "All source code, assets, and infrastructure belong to you. We hand over everything with full documentation.",
  },
];

const plans = [
  {
    name: "Starter",
    price: "₹89,999",
    duration: "Starting price",
    color: "#5540C7",
    description: "Ideal for landing pages, portfolios, or simple business websites with a clean, professional look.",
    features: [
      "Up to 5 pages",
      "Responsive design",
      "Basic contact form",
      "SEO setup",
      "1 month post-launch support",
    ],
    cta: "Get a Quote",
    highlight: false,
  },
  {
    name: "Product",
    price: "₹139,999",
    duration: "Starting price",
    color: "#240BAD",
    description: "For web apps, mobile apps, or SaaS products that need a full-stack build with auth and a database.",
    features: [
      "Custom web or mobile app",
      "User auth & roles",
      "Database design & integration",
      "REST API development",
      "Admin dashboard",
      "3 months post-launch support",
      "Full source code handover",
    ],
    cta: "Get a Quote",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    duration: "Tailored to your scope",
    color: "#8a75f6",
    description: "Complex platforms, blockchain products, AI solutions, or multi-service systems with ongoing maintenance.",
    features: [
      "End-to-end custom development",
      "AI / ML / Blockchain ready",
      "Microservices & cloud infra",
      "Performance & security audits",
      "Dedicated project manager",
      "Ongoing retainer available",
      "SLA-backed support",
    ],
    cta: "Let's Talk",
    highlight: false,
  },
];

const process = [
  { num: "01", title: "Discovery Call", desc: "We spend 30 minutes understanding your idea, goals, and constraints — no jargon, just clarity." },
  { num: "02", title: "Proposal & Scope", desc: "We send a clear proposal: deliverables, timeline, and a fixed or milestone-based price." },
  { num: "03", title: "Design & Build", desc: "We design, develop, and iterate in short sprints — sharing demos at every major milestone." },
  { num: "04", title: "Test & Launch", desc: "Thorough QA across devices and environments, then a smooth deployment to production." },
  { num: "05", title: "Handover & Support", desc: "Full code handover with documentation, plus dedicated post-launch support." },
];

const DevServices = () => {
  const [copied, setCopied] = useState(null);

  const handleCopy = (e, text, key) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className={styles.page}>

      {/* ─── HERO ─── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroBadge}>
            <FiCode /> &nbsp; Software Development Services
          </div>
          <h1 className={styles.heroTitle}>
            We Build <span className={styles.highlight}>Apps &amp; Websites</span> That Actually Work
          </h1>
          <p className={styles.heroSubtitle}>
            Blockenzyme turns your ideas into production-ready digital products — web apps, mobile apps, blockchain platforms, and AI-powered solutions — at low cost and with enterprise-grade reliability.
          </p>
          <div className={styles.heroBtns}>
            <a href="#contact" className={styles.primaryBtn}>
              Start a Project <FiArrowRight />
            </a>
            <a href="#services" className={styles.secondaryBtn}>
              View Services <FiArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className={styles.statsBar}>
        <div className={styles.statsInner}>
          <div className={styles.stat}><span className={styles.statNum}>30+</span><span className={styles.statLabel}>Products Delivered</span></div>
          <div className={styles.statDivider} />
          <div className={styles.stat}><span className={styles.statNum}>6</span><span className={styles.statLabel}>Tech Domains</span></div>
          <div className={styles.statDivider} />
          <div className={styles.stat}><span className={styles.statNum}>2–6 wks</span><span className={styles.statLabel}>Avg. MVP Delivery</span></div>
          <div className={styles.statDivider} />
          <div className={styles.stat}><span className={styles.statNum}>100%</span><span className={styles.statLabel}>Code Ownership</span></div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section className={styles.section} id="services">
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>What We Build</h2>
            <p className={styles.sectionSubtitle}>
              Full-spectrum software development — from a simple website to a complex decentralised application.
            </p>
          </div>
          <div className={styles.servicesGrid}>
            {services.map((svc, i) => (
              <div key={i} className={styles.serviceCard}>
                <div className={styles.serviceCardTop}>
                  <span className={styles.serviceIcon} style={{ color: svc.color, background: svc.color + "18" }}>
                    {svc.icon}
                  </span>
                  <span className={styles.serviceTag}>{svc.tag}</span>
                </div>
                <h3 className={styles.serviceTitle}>{svc.title}</h3>
                <p className={styles.serviceDesc}>{svc.description}</p>
                <ul className={styles.serviceList}>
                  {svc.highlights.map((h, j) => (
                    <li key={j} className={styles.serviceListItem}>
                      <FiCheck className={styles.checkIcon} style={{ color: svc.color }} />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHY US ─── */}
      <section className={styles.whySection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Why Choose Blockenzyme</h2>
            <p className={styles.sectionSubtitle}>We're not a big agency with high overheads. We're a tight team that ships.</p>
          </div>
          <div className={styles.whyGrid}>
            {whyUs.map((item, i) => (
              <div key={i} className={styles.whyCard}>
                <div className={styles.whyIcon}>{item.icon}</div>
                <h4 className={styles.whyTitle}>{item.title}</h4>
                <p className={styles.whyDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS ─── */}
      <section className={styles.section} id="process">
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>How We Work</h2>
            <p className={styles.sectionSubtitle}>
              A simple, transparent process from first call to final handover.
            </p>
          </div>
          <div className={styles.processGrid}>
            {process.map((step, i) => (
              <div key={i} className={styles.processStep}>
                <div className={styles.processNum}>{step.num}</div>
                <h4 className={styles.processTitle}>{step.title}</h4>
                <p className={styles.processDesc}>{step.desc}</p>
                {i < process.length - 1 && <div className={styles.processArrow}><FiArrowRight /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className={styles.whySection} id="pricing">
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Engagement Pricing</h2>
            <p className={styles.sectionSubtitle}>
              Transparent starting prices. Every project is quoted based on your actual requirements — no surprises.
            </p>
          </div>
          <div className={styles.plansGrid}>
            {plans.map((plan, i) => (
              <div
                key={i}
                className={`${styles.planCard} ${plan.highlight ? styles.planCardHighlight : ""}`}
              >
                {plan.highlight && (
                  <div className={styles.popularBadge}>Most Popular</div>
                )}
                <div className={styles.planName}>{plan.name}</div>
                <div className={styles.planPrice}>{plan.price}</div>
                <div className={styles.planDuration}>{plan.duration}</div>
                <p className={styles.planDesc}>{plan.description}</p>
                <ul className={styles.planFeatures}>
                  {plan.features.map((f, j) => (
                    <li key={j} className={styles.planFeatureItem}>
                      <FiCheck className={styles.checkIcon} style={{ color: plan.color }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="#contact" className={`${styles.planBtn} ${plan.highlight ? styles.planBtnPrimary : styles.planBtnOutline}`}
                  style={plan.highlight ? {} : { borderColor: plan.color, color: plan.color }}>
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
          <p className={styles.pricingNote}>
            * Prices are indicative starting points. Final cost depends on features, integrations, and timeline. Contact us for a free, detailed quote.
          </p>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className={styles.contactSection} id="contact">
        <div className={styles.contactInner}>
          <div className={styles.contactLeft}>
            <div className={styles.contactEyebrow}>Get in Touch</div>
            <h2 className={styles.contactTitle}>Ready to Build Something Great?</h2>
            <p className={styles.contactSubtitle}>
              Tell us about your project and we'll get back to you within 24 hours with a free consultation and rough estimate.
            </p>
            <div className={styles.contactCards}>
              <a href="mailto:swapnilkole7500@gmail.com" className={styles.contactCard}>
                <div className={styles.contactCardIcon}><FiMail /></div>
                <div className={styles.contactCardText}>
                  <div className={styles.contactCardLabel}>Email Us</div>
                  <div className={styles.contactCardValue}>swapnilkole7500@gmail.com</div>
                </div>
                <button
                  className={styles.copyBtn}
                  onClick={(e) => handleCopy(e, "swapnilkole7500@gmail.com", "email")}
                  title="Copy email"
                >
                  {copied === "email" ? <FiCheckCircle className={styles.copiedIcon} /> : <FiCopy />}
                </button>
              </a>
              <a href="tel:+918328217163" className={styles.contactCard}>
                <div className={styles.contactCardIcon}><FiPhone /></div>
                <div className={styles.contactCardText}>
                  <div className={styles.contactCardLabel}>Call / WhatsApp</div>
                  <div className={styles.contactCardValue}>+91 83282 17163</div>
                </div>
                <button
                  className={styles.copyBtn}
                  onClick={(e) => handleCopy(e, "+918328217163", "phone")}
                  title="Copy number"
                >
                  {copied === "phone" ? <FiCheckCircle className={styles.copiedIcon} /> : <FiCopy />}
                </button>
              </a>
            </div>
          </div>
          <div className={styles.contactRight}>
            <div className={styles.contactInfoBox}>
              <h4 className={styles.infoBoxTitle}>What happens next?</h4>
              <ol className={styles.infoBoxSteps}>
                <li><span className={styles.infoBoxNum}>1</span>You reach out with your project idea</li>
                <li><span className={styles.infoBoxNum}>2</span>We schedule a free 30-min discovery call</li>
                <li><span className={styles.infoBoxNum}>3</span>We send a detailed proposal &amp; quote</li>
                <li><span className={styles.infoBoxNum}>4</span>We kick off &amp; start building!</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DevServices;
